import { useState, useEffect } from 'react';
import { isLikelyDesktopSiteMode, readSignals } from '../utils/desktopSiteMode';

/**
 * Warns the user when Chrome's "Request desktop site" mode is forcing this phone to
 * render at desktop width (everything tiny). The app cannot reliably override the
 * forced viewport — desktop-site intentionally ignores <meta viewport> — so the
 * mitigation is to tell the user how to turn it off.
 *
 * We DO make one cheap best-effort attempt to re-assert width=device-width on mount
 * (harmless if ignored); if it actually takes effect on some browser/version, the
 * banner self-dismisses on the next resize. Whether that works is browser-dependent
 * and must be confirmed on a real device — the banner is the reliable fallback.
 *
 * Re-evaluates on resize/orientation so it appears/disappears as the user toggles
 * desktop-site or rotates.
 */
export default function DesktopSiteBanner() {
  const [active, setActive] = useState(false);

  useEffect(() => {
    // Best-effort: re-assert device-width once in case the browser honors it.
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      const desired = 'width=device-width, initial-scale=1.0, viewport-fit=cover';
      if (meta.getAttribute('content') !== desired) meta.setAttribute('content', desired);
    }

    const evaluate = () => setActive(isLikelyDesktopSiteMode(readSignals()));
    evaluate();

    // Debounced re-check on viewport changes.
    let t: number | undefined;
    const onChange = () => {
      window.clearTimeout(t);
      t = window.setTimeout(evaluate, 200);
    };
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);

  if (!active) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100000,
        background: '#b45309', color: '#fff',
        padding: '10px max(12px, env(safe-area-inset-right)) 10px max(12px, env(safe-area-inset-left))',
        paddingTop: 'max(10px, env(safe-area-inset-top))',
        fontSize: 14, lineHeight: 1.45, textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
      }}
    >
      <strong>This app is in &ldquo;Desktop site&rdquo; mode</strong> — that&rsquo;s why everything looks tiny.
      <br />
      In your browser menu (<span style={{ fontWeight: 700 }}>&#8942;</span>), turn{' '}
      <strong>Desktop site</strong> <strong>off</strong>, then reload.
    </div>
  );
}
